import React from "react";
import { useTranslation } from "react-i18next";
import type { EventSegment } from "../hooks/useTimelineEvents";
import { formatOrgLocaleTime } from "@/shared/utils/date/org-time";

interface EventTooltipProps {
  hoveredEvent: EventSegment | null;
  hoverPos: { x: number; y: number } | null;
  isResizing: boolean;
  isMoving: boolean;
}

export const EventTooltip: React.FC<EventTooltipProps> = ({
  hoveredEvent,
  hoverPos,
  isResizing,
  isMoving,
}) => {
  const { t } = useTranslation("machines");

  if (!hoveredEvent || !hoverPos || isResizing || isMoving) return null;

  return (
    <foreignObject
      x={hoverPos.x}
      y={hoverPos.y - 35}
      width={200}
      height={40}
      style={{ pointerEvents: "none" }}
    >
      <div
        style={{
          backgroundColor: "var(--timeline-tooltip-bg)",
          color: "var(--timeline-tooltip-text)",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          maxWidth: "200px",
          whiteSpace: "nowrap",
        }}
      >
        {hoveredEvent.event?.state ?? t("timeline.noStatus")}
      </div>
    </foreignObject>
  );
};

interface ResizeTooltipProps {
  isEditMode: boolean;
  isResizing: boolean;
  mousePosition: { x: number; y: number } | null;
  tempEventTimes: { start: number; end: number } | null;
  resizeHandle: "start" | "end" | null;
  scaledWidth: number;
}

export const ResizeTooltip: React.FC<ResizeTooltipProps> = ({
  isEditMode,
  isResizing,
  mousePosition,
  tempEventTimes,
  resizeHandle,
  scaledWidth,
}) => {
  if (!isEditMode || !isResizing || !mousePosition || !tempEventTimes || !resizeHandle) {
    return null;
  }

  return (
    <foreignObject
      x={Math.max(0, Math.min(scaledWidth - 150, mousePosition.x - 75))}
      y={Math.max(10, mousePosition.y - 45)}
      width={150}
      height={40}
      style={{ pointerEvents: "none" }}
    >
      <div
        style={{
          backgroundColor: "rgba(59, 130, 246, 0.95)",
          color: "#ffffff",
          padding: "6px 10px",
          borderRadius: "6px",
          fontSize: "13px",
          fontWeight: "600",
          textAlign: "center",
          whiteSpace: "nowrap",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
      >
        {formatOrgLocaleTime(
          resizeHandle === "start" ? tempEventTimes.start : tempEventTimes.end,
          {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          },
        )}
      </div>
    </foreignObject>
  );
};

interface MoveTooltipProps {
  isEditMode: boolean;
  isMoving: boolean;
  mousePosition: { x: number; y: number } | null;
  tempEventTimes: { start: number; end: number } | null;
  scaledWidth: number;
}

export const MoveTooltip: React.FC<MoveTooltipProps> = ({
  isEditMode,
  isMoving,
  mousePosition,
  tempEventTimes,
  scaledWidth,
}) => {
  if (!isEditMode || !isMoving || !mousePosition || !tempEventTimes) {
    return null;
  }

  return (
    <foreignObject
      x={Math.max(0, Math.min(scaledWidth - 200, mousePosition.x - 100))}
      y={Math.max(10, mousePosition.y - 45)}
      width={200}
      height={40}
      style={{ pointerEvents: "none" }}
    >
      <div
        style={{
          backgroundColor: "rgba(139, 92, 246, 0.95)",
          color: "#ffffff",
          padding: "6px 10px",
          borderRadius: "6px",
          fontSize: "13px",
          fontWeight: "600",
          textAlign: "center",
          whiteSpace: "nowrap",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
      >
        {formatOrgLocaleTime(tempEventTimes.start, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })}
        {" - "}
        {formatOrgLocaleTime(tempEventTimes.end, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })}
      </div>
    </foreignObject>
  );
};
